import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { toast } from "sonner";

export interface CartItem {
  id: string;
  title: string;
  price: number;
  originalPrice?: number;
  image: string;
  type: "guide" | "map" | "bundle";
  quantity: number;
  bundleDiscount?: number;
}

interface CartContextType {
  items: CartItem[];
  itemCount: number;
  subtotal: number;
  discount: number;
  total: number;
  addItem: (item: Omit<CartItem, "quantity">) => void;
  removeItem: (id: string) => void;
  updateQuantity: (id: string, quantity: number) => void;
  clearCart: () => void;
  addBundle: (guideItem: Omit<CartItem, "quantity">, mapItem: Omit<CartItem, "quantity">) => void;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

const CART_STORAGE_KEY = "travel-boost-cart";

export function CartProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<CartItem[]>(() => {
    if (typeof window !== "undefined") {
      const saved = localStorage.getItem(CART_STORAGE_KEY);
      return saved ? JSON.parse(saved) : [];
    }
    return [];
  });

  // Persist to localStorage
  useEffect(() => {
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);
  
  const subtotal = items.reduce((sum, item) => sum + item.price * item.quantity, 0);
  
  const discount = items.reduce((sum, item) => {
    if (item.bundleDiscount) {
      return sum + (item.bundleDiscount * item.quantity);
    }
    return sum;
  }, 0);
  
  const total = subtotal - discount;

  const addItem = (newItem: Omit<CartItem, "quantity">) => {
    setItems((current) => {
      const existingIndex = current.findIndex((item) => item.id === newItem.id);
      
      if (existingIndex >= 0) {
        const updated = [...current];
        updated[existingIndex].quantity += 1;
        toast.success("הכמות עודכנה בסל");
        return updated;
      }
      
      toast.success("המוצר נוסף לסל");
      return [...current, { ...newItem, quantity: 1 }];
    });
  };

  const removeItem = (id: string) => {
    setItems((current) => current.filter((item) => item.id !== id));
    toast.success("המוצר הוסר מהסל");
  };

  const updateQuantity = (id: string, quantity: number) => {
    if (quantity < 1) {
      removeItem(id);
      return;
    }
    
    setItems((current) =>
      current.map((item) =>
        item.id === id ? { ...item, quantity } : item
      )
    );
  };

  const clearCart = () => {
    setItems([]);
  };

  const addBundle = (
    guideItem: Omit<CartItem, "quantity">,
    mapItem: Omit<CartItem, "quantity">
  ) => {
    // Calculate 10% discount on the combined price
    const combinedPrice = guideItem.price + mapItem.price;
    const bundleDiscountAmount = Math.round(combinedPrice * 0.1);
    
    const bundleItem: Omit<CartItem, "quantity"> = {
      id: `bundle-${guideItem.id}-${mapItem.id}`,
      title: `${guideItem.title} + ${mapItem.title}`,
      price: combinedPrice,
      image: guideItem.image,
      type: "bundle",
      bundleDiscount: bundleDiscountAmount,
    };
    
    setItems((current) => {
      const existingIndex = current.findIndex((item) => item.id === bundleItem.id);
      
      if (existingIndex >= 0) {
        const updated = [...current];
        updated[existingIndex].quantity += 1;
        toast.success("הבאנדל עודכן בסל");
        return updated;
      }
      
      toast.success("הבאנדל נוסף לסל עם 10% הנחה!");
      return [...current, { ...bundleItem, quantity: 1 }];
    });
  };

  return (
    <CartContext.Provider
      value={{
        items,
        itemCount,
        subtotal,
        discount,
        total,
        addItem,
        removeItem,
        updateQuantity,
        clearCart,
        addBundle,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return context;
}
